import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  TimeEntry,
  WorkRolesDict,
  WorkTypesDict,
  ChargeCodesDict,
  ProjectTicket,
  CreateTimeEntryPayload,
} from '../types/api.types';
import { apiService, BadRequestError } from '../services/apiService';
import {
  getUserTimezone,
  getTimezoneLabel,
  utcToLocalTime,
  localToUtcIso,
  calculateHoursDiff,
  extractDatePortion,
} from '../utils/timeUtils';
import './TimeEntryModal.css';

type ChargeType = 'ChargeCode' | 'ProjectTicket';
type BillableOption = 'DoNotBill' | 'Billable' | 'NoCharge';

interface TimeEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  memberIdentifier: string;
  timeSheetId: number;
  workRoles: WorkRolesDict;
  workTypes: WorkTypesDict;
  chargeCodes: ChargeCodesDict;
  copyFromEntry?: TimeEntry | null;
}

const TimeEntryModal: React.FC<TimeEntryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  memberIdentifier,
  timeSheetId,
  workRoles,
  workTypes,
  chargeCodes,
  copyFromEntry,
}) => {
  // Form state
  const [date, setDate] = useState('');
  const [chargeType, setChargeType] = useState<ChargeType>('ChargeCode');
  const [selectedChargeCodeId, setSelectedChargeCodeId] = useState<number>(0);
  const [projectNumber, setProjectNumber] = useState('');
  const [projectTickets, setProjectTickets] = useState<ProjectTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<ProjectTicket | null>(null);
  const [selectedWorkRoleId, setSelectedWorkRoleId] = useState<number>(0);
  const [selectedWorkTypeId, setSelectedWorkTypeId] = useState<number>(0);
  const [actualHours, setActualHours] = useState<string>('');
  const [billableOption, setBillableOption] = useState<BillableOption>('DoNotBill');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [deduct, setDeduct] = useState('');

  const userTz = useMemo(() => getUserTimezone(), []);
  const tzLabel = useMemo(() => getTimezoneLabel(userTz), [userTz]);
  const skipNextCalcRef = useRef(true);

  const [chargeCodeFilter, setChargeCodeFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when modal opens or copyFromEntry changes
  useEffect(() => {
    if (isOpen) {
      if (copyFromEntry) {
        // Pre-fill from copied entry
        setDate(extractDatePortion(copyFromEntry.timeStart) || new Date().toISOString().split('T')[0]);
        setStartTime(utcToLocalTime(copyFromEntry.timeStart, userTz));
        setEndTime(utcToLocalTime(copyFromEntry.timeEnd, userTz));
        setDeduct(copyFromEntry.hoursDeduct ? copyFromEntry.hoursDeduct.toString() : '');
        skipNextCalcRef.current = true;
        setChargeType(copyFromEntry.chargeToType);
        if (copyFromEntry.chargeToType === 'ChargeCode') {
          setSelectedChargeCodeId(copyFromEntry.chargeToId);
          setProjectNumber('');
          setProjectTickets([]);
          setSelectedTicket(null);
        } else {
          // For ProjectTicket, search and auto-select
          const projectId = copyFromEntry.project?.id?.toString() || '';
          setProjectNumber(projectId);
          setSelectedChargeCodeId(0);
          
          // Auto-search and select the ticket
          if (projectId) {
            setIsSearching(true);
            apiService.searchProjectTickets(projectId).then((tickets) => {
              setProjectTickets(tickets);
              // Try to find and select the matching ticket
              const matchingTicket = tickets.find(t => t.id === copyFromEntry.chargeToId);
              if (matchingTicket) {
                setSelectedTicket(matchingTicket);
              } else {
                // Fallback to manually constructed ticket if not in list
                setSelectedTicket({
                  id: copyFromEntry.chargeToId,
                  summary: copyFromEntry.ticket?.summary || '',
                  company: copyFromEntry.company,
                  project: copyFromEntry.project,
                });
              }
            }).catch((err) => {
              console.error('Error searching tickets:', err);
              // Fallback to manually constructed ticket
              setSelectedTicket({
                id: copyFromEntry.chargeToId,
                summary: copyFromEntry.ticket?.summary || '',
                company: copyFromEntry.company,
                project: copyFromEntry.project,
              });
            }).finally(() => {
              setIsSearching(false);
            });
          } else {
            setSelectedTicket({
              id: copyFromEntry.chargeToId,
              summary: copyFromEntry.ticket?.summary || '',
              company: copyFromEntry.company,
              project: copyFromEntry.project,
            });
          }
        }
        setSelectedWorkRoleId(copyFromEntry.workRole?.id ?? 0);
        setSelectedWorkTypeId(copyFromEntry.workType?.id ?? 0);
        setActualHours(parseFloat(copyFromEntry.actualHours.toString()).toString());
        setBillableOption(copyFromEntry.billableOption || 'DoNotBill');
      } else {
        // Reset form
        setDate(new Date().toISOString().split('T')[0]);
        setChargeType('ChargeCode');
        setSelectedChargeCodeId(0);
        setProjectNumber('');
        setProjectTickets([]);
        setSelectedTicket(null);
        setSelectedWorkRoleId(0);
        setSelectedWorkTypeId(0);
        setActualHours('');
        setBillableOption('DoNotBill');
        setStartTime('');
        setEndTime('');
        setDeduct('');
        skipNextCalcRef.current = true;
        setChargeCodeFilter('');
      }
      setError(null);
    }
  }, [isOpen, copyFromEntry]);

  // Auto-calculate actual hours from start/end time and deduct
  useEffect(() => {
    if (skipNextCalcRef.current) {
      skipNextCalcRef.current = false;
      return;
    }
    if (startTime && endTime) {
      const diff = calculateHoursDiff(startTime, endTime);
      const deductVal = parseFloat(deduct) || 0;
      const calculated = Math.max(0, diff - deductVal);
      setActualHours(parseFloat(calculated.toFixed(2)).toString());
    }
  }, [startTime, endTime, deduct]);

  // Filtered charge codes
  const filteredChargeCodes = useMemo(() => {
    const codes = Object.values(chargeCodes);
    if (!chargeCodeFilter) return codes;
    const filter = chargeCodeFilter.toLowerCase();
    return codes.filter(
      (code) =>
        code.name.toLowerCase().includes(filter) ||
        code.company?.name?.toLowerCase().includes(filter)
    );
  }, [chargeCodes, chargeCodeFilter]);

  // Search for project tickets
  const handleSearchTickets = async () => {
    if (!projectNumber.trim()) return;
    setIsSearching(true);
    setError(null);
    try {
      const tickets = await apiService.searchProjectTickets(projectNumber.trim());
      setProjectTickets(tickets);
      if (tickets.length === 0) {
        setError('No tickets found for this project');
      }
    } catch (err) {
      console.error('Error searching tickets:', err);
      setError('Failed to search for tickets');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle hours input
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setActualHours(value);
    }
  };

  // Handle deduct input
  const handleDeductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDeduct(value);
    }
  };

  // Validate and save
  const handleSave = async () => {
    setError(null);

    // Validation
    if (!date) {
      setError('Please select a date');
      return;
    }
    if (chargeType === 'ChargeCode' && !selectedChargeCodeId) {
      setError('Please select a charge code');
      return;
    }
    if (chargeType === 'ProjectTicket' && !selectedTicket) {
      setError('Please select a project ticket');
      return;
    }
    if (!selectedWorkRoleId) {
      setError('Please select a work role');
      return;
    }
    if (!selectedWorkTypeId) {
      setError('Please select a work type');
      return;
    }
    if (!actualHours || parseFloat(actualHours) <= 0) {
      setError('Please enter valid actual hours');
      return;
    }

    setIsSaving(true);

    try {
      let payload: CreateTimeEntryPayload;

      if (chargeType === 'ChargeCode') {
        payload = {
          timeStart: startTime ? localToUtcIso(date, startTime, userTz) : date,
          timeEnd: endTime ? localToUtcIso(date, endTime, userTz) : undefined,
          company: { identifier: 'ZDI' },
          companyType: 'Customer',
          chargeToId: selectedChargeCodeId,
          chargeToType: 'ChargeCode',
          member: { identifier: memberIdentifier },
          workType: { id: selectedWorkTypeId },
          workRole: { id: selectedWorkRoleId },
          actualHours: parseFloat(actualHours),
          hoursDeduct: deduct ? parseFloat(deduct) : undefined,
          timeSheet: { id: timeSheetId },
          billableOption: billableOption,
        };
      } else {
        payload = {
          timeStart: startTime ? localToUtcIso(date, startTime, userTz) : date,
          timeEnd: endTime ? localToUtcIso(date, endTime, userTz) : undefined,
          company: { identifier: selectedTicket!.company?.identifier || '' },
          companyType: 'Customer',
          chargeToId: selectedTicket!.id,
          chargeToType: 'ProjectTicket',
          member: { identifier: memberIdentifier },
          workType: { id: selectedWorkTypeId },
          workRole: { id: selectedWorkRoleId },
          actualHours: parseFloat(actualHours),
          hoursDeduct: deduct ? parseFloat(deduct) : undefined,
          timeSheet: { id: timeSheetId },
          billableOption: billableOption,
        };
      }

      await apiService.createTimeEntry(payload);
      await onSave();
      onClose();
    } catch (err) {
      console.error('Error creating entry:', err);
      if (err instanceof BadRequestError) {
        alert(err.message);
      } else {
        const message = err instanceof Error ? err.message : 'Failed to create time entry';
        setError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{copyFromEntry ? 'Copy Time Entry' : 'Add New Time Entry'}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Charge Type</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="chargeType"
                  value="ChargeCode"
                  checked={chargeType === 'ChargeCode'}
                  onChange={() => setChargeType('ChargeCode')}
                />
                Charge Code
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="chargeType"
                  value="ProjectTicket"
                  checked={chargeType === 'ProjectTicket'}
                  onChange={() => setChargeType('ProjectTicket')}
                />
                Project
              </label>
            </div>
          </div>

          {chargeType === 'ChargeCode' && (
            <div className="form-group">
              <label>Charge Code</label>
              <input
                type="text"
                placeholder="Filter charge codes..."
                value={chargeCodeFilter}
                onChange={(e) => setChargeCodeFilter(e.target.value)}
                className="form-input filter-input"
              />
              <select
                value={selectedChargeCodeId}
                onChange={(e) => setSelectedChargeCodeId(parseInt(e.target.value, 10))}
                className="form-select"
                size={5}
              >
                <option value={0}>-- Select Charge Code --</option>
                {filteredChargeCodes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {code.company?.name || 'Unknown'} / {code.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {chargeType === 'ProjectTicket' && (
            <div className="form-group">
              <label>Project Number</label>
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Enter project ID"
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                  className="form-input"
                />
                <button
                  className="btn btn-secondary"
                  onClick={handleSearchTickets}
                  disabled={isSearching}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {projectTickets.length > 0 && (
                <select
                  value={selectedTicket?.id ?? 0}
                  onChange={(e) => {
                    const ticket = projectTickets.find(
                      (t) => t.id === parseInt(e.target.value, 10)
                    );
                    setSelectedTicket(ticket || null);
                  }}
                  className="form-select"
                  size={5}
                >
                  <option value={0}>-- Select Ticket --</option>
                  {projectTickets.map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.company?.name} / {ticket.project?.name} / {ticket.summary}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Work Role</label>
              <select
                value={selectedWorkRoleId}
                onChange={(e) => setSelectedWorkRoleId(parseInt(e.target.value, 10))}
                className="form-select"
              >
                <option value={0}>-- Select --</option>
                {Object.values(workRoles).sort((a, b) => a.name.localeCompare(b.name)).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Work Type</label>
              <select
                value={selectedWorkTypeId}
                onChange={(e) => setSelectedWorkTypeId(parseInt(e.target.value, 10))}
                className="form-select"
              >
                <option value={0}>-- Select --</option>
                {Object.values(workTypes).sort((a, b) => a.name.localeCompare(b.name)).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Time ({tzLabel})</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>End Time ({tzLabel})</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label>Deduct (hrs)</label>
              <input
                type="text"
                value={deduct}
                onChange={handleDeductChange}
                placeholder="0.00"
                className="form-input"
                inputMode="decimal"
              />
            </div>
            <div className="form-group">
              <label>Actual Hours</label>
              <input
                type="text"
                value={actualHours}
                onChange={handleHoursChange}
                placeholder="0.00"
                className="form-input"
                inputMode="decimal"
              />
            </div>
            <div className="form-group">
              <label>Billable Option</label>
              <select
                value={billableOption}
                onChange={(e) => setBillableOption(e.target.value as BillableOption)}
                className="form-select"
              >
                <option value="DoNotBill">Do Not Bill</option>
                <option value="Billable">Billable</option>
                <option value="NoCharge">No Charge</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Member</label>
            <input
              type="text"
              value={memberIdentifier}
              disabled
              className="form-input disabled"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeEntryModal;
